import { useCallback, useEffect, useMemo, useState } from 'react'
import { MessageCircle, Search, X, Circle } from 'lucide-react'
import { adminApi, unwrap } from '../services/api'
import { useColors } from '../hooks/useColors'
import { useToast } from '../contexts/ToastContext'
import EmptyState from '../components/shared/EmptyState'
import ErrorState from '../components/shared/ErrorState'

type ComplaintStatus = 'open' | 'in_progress' | 'resolved' | 'closed'
type ComplaintPriority = 'low' | 'medium' | 'high' | 'urgent'

type Complaint = {
  id: string
  created_at?: string | null
  customer_id?: string
  customer_name?: string | null
  customer_phone?: string | null
  order_id?: string | null
  order_number?: string | null
  category?: string | null
  priority?: ComplaintPriority | string | null
  status?: ComplaintStatus | string | null
  description?: string | null
  resolution?: string | null
  refund_amount?: number | string | null
  updated_at?: string | null
  timeline?: Array<{ label?: string; status?: string; created_at?: string }>
}

type Customer = {
  id: string
  name?: string | null
  phone?: string | null
}

type OrderOption = {
  id: string
  order_number?: string
  customer_name?: string | null
}

type ComplaintStats = {
  open?: number | string
  in_progress?: number | string
  resolved_today?: number | string
  avg_resolution_hours?: number | string
}

const CARD_SHADOW = '0px 2px 8px rgba(0,0,0,0.04)'
const categories = ['damage', 'missing_item', 'late_delivery', 'quality', 'billing', 'other']
const priorities: ComplaintPriority[] = ['low', 'medium', 'high', 'urgent']
const statuses: ComplaintStatus[] = ['open', 'in_progress', 'resolved', 'closed']

const toTitle = (value?: string | null) =>
  String(value || '')
    .replace(/_/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ') || '-'

const formatDate = (value?: string | null) => {
  if (!value) {
    return '-'
  }

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return '-'
  }

  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })
}

const getComplaintList = (payload: unknown): Complaint[] => {
  if (Array.isArray(payload)) {
    return payload as Complaint[]
  }

  const shaped = payload as { complaints?: Complaint[]; data?: Complaint[] }
  if (Array.isArray(shaped?.complaints)) {
    return shaped.complaints
  }

  return Array.isArray(shaped?.data) ? shaped.data : []
}

const getOrderList = (payload: unknown): OrderOption[] => {
  if (Array.isArray(payload)) {
    return payload as OrderOption[]
  }

  const shaped = payload as { orders?: OrderOption[]; data?: { orders?: OrderOption[] } }
  if (Array.isArray(shaped?.orders)) {
    return shaped.orders
  }

  return Array.isArray(shaped?.data?.orders) ? shaped.data.orders : []
}

const getCustomers = (payload: unknown): Customer[] => {
  if (Array.isArray(payload)) {
    return payload as Customer[]
  }

  const shaped = payload as { customers?: Customer[]; data?: { customers?: Customer[] } }
  if (Array.isArray(shaped?.customers)) {
    return shaped.customers
  }

  return Array.isArray(shaped?.data?.customers) ? shaped.data.customers : []
}

const priorityStyle = (priority?: string | null) => {
  const normalized = String(priority || 'low').toLowerCase()
  if (normalized === 'urgent') {
    return { label: 'Urgent', color: '#991B1B', background: '#FEF2F2', border: '#FCA5A5' }
  }
  if (normalized === 'high') {
    return { label: 'High', color: '#92400E', background: '#FEF3C7', border: '#FCD34D' }
  }
  if (normalized === 'medium') {
    return { label: 'Medium', color: '#854D0E', background: '#FEFCE8', border: '#FDE68A' }
  }
  return { label: 'Low', color: '#15803D', background: '#F0FDF4', border: '#86EFAC' }
}

const statusStyle = (status?: string | null) => {
  const normalized = String(status || 'open').toLowerCase()
  if (normalized === 'resolved' || normalized === 'closed') {
    return { color: '#15803D', background: '#F0FDF4', border: '#86EFAC' }
  }
  if (normalized === 'in_progress') {
    return { color: '#92400E', background: '#FEF3C7', border: '#FCD34D' }
  }
  return { color: '#991B1B', background: '#FEF2F2', border: '#FCA5A5' }
}

export default function ComplaintsPage() {
  const colors = useColors()
  const { addToast } = useToast()
  const [complaints, setComplaints] = useState<Complaint[]>([])
  const [stats, setStats] = useState<ComplaintStats>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [priorityFilter, setPriorityFilter] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedComplaint, setSelectedComplaint] = useState<Complaint | null>(null)
  const [showLogModal, setShowLogModal] = useState(false)
  const [customerPhone, setCustomerPhone] = useState('')
  const [customer, setCustomer] = useState<Customer | null>(null)
  const [customerName, setCustomerName] = useState('')
  const [orderSearch, setOrderSearch] = useState('')
  const [orders, setOrders] = useState<OrderOption[]>([])
  const [formData, setFormData] = useState({
    order_id: '',
    category: 'quality',
    priority: 'medium' as ComplaintPriority,
    description: '',
  })
  const [detailStatus, setDetailStatus] = useState<ComplaintStatus>('in_progress')
  const [resolution, setResolution] = useState('')
  const [refundAmount, setRefundAmount] = useState('')
  const [saving, setSaving] = useState(false)
  const [modalError, setModalError] = useState('')

  const loadComplaints = useCallback(async () => {
    setLoading(true)
    setError('')

    try {
      const complaintsResponse = await adminApi.getComplaints({
        status: statusFilter || undefined,
        category: categoryFilter || undefined,
        priority: priorityFilter || undefined,
        search: searchTerm.trim() || undefined,
      })

      setComplaints(getComplaintList(unwrap(complaintsResponse)))

      try {
        const statsResponse = await adminApi.getComplaintStats()
        setStats(unwrap<ComplaintStats>(statsResponse) || {})
      } catch (statsError) {
        console.warn('Complaint stats failed to load', statsError)
        setStats({})
      }
    } catch (loadError) {
      setError('Unable to load complaints. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [categoryFilter, priorityFilter, searchTerm, statusFilter])

  useEffect(() => {
    void loadComplaints()
  }, [loadComplaints])

  useEffect(() => {
    if (!selectedComplaint) {
      return
    }

    setDetailStatus((selectedComplaint.status as ComplaintStatus) || 'in_progress')
    setResolution(selectedComplaint.resolution || '')
    setRefundAmount(selectedComplaint.refund_amount ? String(selectedComplaint.refund_amount) : '')
  }, [selectedComplaint])

  const filteredStats = useMemo(
    () => [
      { label: 'Open', value: Number(stats.open || 0), color: '#991B1B', bg: '#FEF2F2' },
      { label: 'In Progress', value: Number(stats.in_progress || 0), color: '#92400E', bg: '#FEF3C7' },
      { label: 'Resolved Today', value: Number(stats.resolved_today || 0), color: '#15803D', bg: '#F0FDF4' },
      { label: 'Avg Resolution Time', value: `${stats.avg_resolution_hours ?? '-'} hrs`, color: colors.textPrimary, bg: colors.surface },
    ],
    [stats, colors],
  )

  const handleSearchCustomer = async () => {
    if (!customerPhone.trim()) {
      setModalError('Enter a customer phone number.')
      return
    }

    setModalError('')
    const response = await adminApi.searchCustomerByPhone(customerPhone.trim())
    const found = getCustomers(unwrap(response))[0] || null
    setCustomer(found)
    setCustomerName(found?.name || '')
  }

  const handleCreateCustomer = async () => {
    if (!customerPhone.trim() || !customerName.trim()) {
      setModalError('Customer name and phone are required.')
      return
    }

    const response = await adminApi.createCustomer({
      name: customerName.trim(),
      phone: customerPhone.trim(),
    })
    const created = unwrap<Customer>(response)
    setCustomer(created)
    setModalError('')
  }

  const handleSearchOrders = async () => {
    const response = await adminApi.getOrders({
      search: orderSearch.trim() || customerPhone.trim() || undefined,
      limit: 10,
    })
    setOrders(getOrderList(unwrap(response)))
  }

  const resetModal = () => {
    setCustomerPhone('')
    setCustomer(null)
    setCustomerName('')
    setOrderSearch('')
    setOrders([])
    setFormData({ order_id: '', category: 'quality', priority: 'medium', description: '' })
    setModalError('')
  }

  const handleCreateComplaint = async () => {
    if (!customer && !customerPhone.trim()) {
      setModalError('Search or create a customer first.')
      return
    }

    if (!formData.description.trim()) {
      setModalError('Description is required.')
      return
    }

    setSaving(true)
    setModalError('')

    try {
      const selectedOrder = orders.find((order) => order.id === formData.order_id)
      await adminApi.createComplaint({
        customer_id: customer?.id,
        customer_phone: customerPhone.trim(),
        order_id: formData.order_id || undefined,
        order_number: selectedOrder?.order_number,
        category: formData.category,
        priority: formData.priority,
        description: formData.description.trim(),
      })
      setShowLogModal(false)
      resetModal()
      await loadComplaints()
    } catch (createError) {
      setModalError('Unable to create complaint. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const handleUpdateComplaint = async (resolveComplaint = false) => {
    if (!selectedComplaint) {
      return
    }

    // Confirm any action that carries a non-zero refund.
    const refund = refundAmount ? Number(refundAmount) : 0
    if (refund > 0) {
      const who = selectedComplaint.customer_name || 'the customer'
      if (!window.confirm(`Issue refund of ₹${refund} to ${who}?`)) {
        return
      }
    }

    setSaving(true)

    try {
      const response = await adminApi.updateComplaint(selectedComplaint.id, {
        status: resolveComplaint ? 'resolved' : detailStatus,
        resolution: resolution.trim() || undefined,
        refund_amount: refundAmount ? Number(refundAmount) : null,
      })
      // Surface the backend's message - e.g. "refund skipped: policy denies…" -
      // which can come back on a 200 and would otherwise be invisible.
      const serverMessage = (response as { message?: string })?.message
      if (serverMessage) {
        const looksLikeWarning = /skip|den|cannot|unable|not\b/i.test(serverMessage)
        addToast(serverMessage, looksLikeWarning ? 'info' : 'success')
      } else {
        addToast(resolveComplaint ? 'Complaint resolved' : 'Complaint updated', 'success')
      }
      const updated = unwrap<Complaint>(response)
      setSelectedComplaint(updated?.id ? updated : { ...selectedComplaint, status: resolveComplaint ? 'resolved' : detailStatus, resolution })
      await loadComplaints()
    } finally {
      setSaving(false)
    }
  }

  const openWhatsApp = () => {
    if (!selectedComplaint?.customer_phone) {
      addToast('Customer phone is not available.', 'error')
      return
    }

    const cleanPhone = selectedComplaint.customer_phone.replace(/[\s\-\+\(\)]/g, '')
    const phoneWithCountry = cleanPhone.startsWith('91') ? cleanPhone : `91${cleanPhone}`
    const message = encodeURIComponent(
      `Hi ${selectedComplaint.customer_name || 'there'}, your Rapidry complaint has been updated. ${resolution || 'We will keep you posted.'}`,
    )
    window.open(`https://wa.me/${phoneWithCountry}?text=${message}`, '_blank')
  }

  if (loading) {
    return (
      <div style={{ display: 'grid', gap: '20px' }}>
        <div className="rapidry-pulse" style={{ height: '120px', borderRadius: '16px', background: colors.border }} />
        <div className="rapidry-pulse" style={{ height: '420px', borderRadius: '16px', background: colors.border }} />
      </div>
    )
  }

  if (error) {
    return <ErrorState message={error} onRetry={() => void loadComplaints()} />
  }

  return (
    <div>
      <section style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '20px' }}>
        {filteredStats.map((item) => (
          <article key={item.label} style={{ background: colors.bgCard, borderRadius: '16px', padding: '20px', boxShadow: CARD_SHADOW }}>
            <p className="font-body" style={{ margin: '0 0 8px', fontSize: '12px', fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase' }}>
              {item.label}
            </p>
            <span className="font-display" style={{ display: 'inline-flex', borderRadius: '999px', padding: '6px 12px', background: item.bg, color: item.color, fontSize: '24px', fontWeight: 700 }}>
              {item.value}
            </span>
          </article>
        ))}
      </section>

      <section style={{ background: colors.bgCard, borderRadius: '16px', padding: '18px', boxShadow: CARD_SHADOW, marginBottom: '20px' }}>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} className="font-body" style={{ height: '40px', border: `1.5px solid ${colors.border}`, borderRadius: '10px', padding: '0 10px' }}>
            <option value="">All Status</option>
            {statuses.map((status) => <option key={status} value={status}>{toTitle(status)}</option>)}
          </select>
          <select value={categoryFilter} onChange={(event) => setCategoryFilter(event.target.value)} className="font-body" style={{ height: '40px', border: `1.5px solid ${colors.border}`, borderRadius: '10px', padding: '0 10px' }}>
            <option value="">All Categories</option>
            {categories.map((category) => <option key={category} value={category}>{toTitle(category)}</option>)}
          </select>
          <select value={priorityFilter} onChange={(event) => setPriorityFilter(event.target.value)} className="font-body" style={{ height: '40px', border: `1.5px solid ${colors.border}`, borderRadius: '10px', padding: '0 10px' }}>
            <option value="">All Priority</option>
            {priorities.map((priority) => <option key={priority} value={priority}>{toTitle(priority)}</option>)}
          </select>
          <div style={{ position: 'relative', flex: 1 }}>
            <Search size={16} color={colors.textMuted} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
            <input value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} placeholder="Search complaints..." className="font-body" style={{ width: '100%', height: '40px', border: `1.5px solid ${colors.border}`, borderRadius: '10px', padding: '0 12px 0 38px' }} />
          </div>
          <button type="button" className="font-body" onClick={() => setShowLogModal(true)} style={{ height: '40px', border: 'none', borderRadius: '10px', background: '#D6B97B', color: colors.textPrimary, padding: '0 18px', fontSize: '14px', fontWeight: 700, cursor: 'pointer' }}>
            Log Complaint
          </button>
        </div>
      </section>

      <section style={{ background: colors.bgCard, borderRadius: '16px', boxShadow: CARD_SHADOW, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: colors.bgSecondary, borderBottom: `1px solid ${colors.border}` }}>
              {['#', 'Date', 'Customer', 'Order', 'Category', 'Priority', 'Status', 'Actions'].map((header) => (
                <th key={header} className="font-body" style={{ padding: '14px 16px', fontSize: '11px', fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase', textAlign: 'left' }}>
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {complaints.length === 0 ? (
              <tr>
                <td colSpan={8}>
                  <EmptyState title="No complaints found" description="Try adjusting your filters." />
                </td>
              </tr>
            ) : (
              complaints.map((complaint, index) => {
                const priority = priorityStyle(complaint.priority)
                const status = statusStyle(complaint.status)

                return (
                  <tr key={complaint.id} onClick={() => setSelectedComplaint(complaint)} style={{ borderBottom: `1px solid ${colors.border}`, cursor: 'pointer' }}>
                    <td className="font-body" style={{ padding: '16px', color: colors.textMuted }}>{index + 1}</td>
                    <td className="font-body" style={{ padding: '16px', color: colors.textSecondary }}>{formatDate(complaint.created_at)}</td>
                    <td className="font-body" style={{ padding: '16px', fontWeight: 600, color: colors.textPrimary }}>{complaint.customer_name || complaint.customer_phone || 'Customer'}</td>
                    <td className="font-body" style={{ padding: '16px', color: colors.textSecondary }}>{complaint.order_number || '-'}</td>
                    <td className="font-body" style={{ padding: '16px', color: colors.textSecondary }}>{toTitle(complaint.category)}</td>
                    <td style={{ padding: '16px' }}>
                      <span className="font-body" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', border: `1px solid ${priority.border}`, background: priority.background, color: priority.color, borderRadius: '999px', padding: '4px 10px', fontSize: '12px', fontWeight: 700 }}>
                        <Circle size={8} fill={priority.color} color={priority.color} aria-hidden />
                        {priority.label}
                      </span>
                    </td>
                    <td style={{ padding: '16px' }}>
                      <span className="font-body" style={{ border: `1px solid ${status.border}`, background: status.background, color: status.color, borderRadius: '999px', padding: '4px 10px', fontSize: '12px', fontWeight: 700 }}>
                        {toTitle(complaint.status)}
                      </span>
                    </td>
                    <td style={{ padding: '16px' }}>
                      <button type="button" className="font-body" onClick={(event) => { event.stopPropagation(); setSelectedComplaint(complaint) }} style={{ border: '1px solid #D6B97B', color: '#D6B97B', background: 'transparent', borderRadius: '8px', padding: '6px 10px', fontWeight: 700, cursor: 'pointer' }}>
                        View
                      </button>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </section>

      {showLogModal ? (
        <div style={{ position: 'fixed', inset: 0, zIndex: 70, background: 'rgba(0,0,0,0.4)', display: 'grid', placeItems: 'center', padding: '24px' }} onClick={() => setShowLogModal(false)}>
          <div onClick={(event) => event.stopPropagation()} style={{ width: '620px', maxHeight: '90vh', overflowY: 'auto', background: colors.bgCard, borderRadius: '20px', padding: '28px', boxShadow: '0px 24px 64px rgba(15,46,42,0.20)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 className="font-display" style={{ margin: 0, fontSize: '20px', color: colors.textPrimary }}>Log Complaint</h2>
              <button type="button" onClick={() => setShowLogModal(false)} style={{ border: 'none', background: 'transparent', cursor: 'pointer' }}><X size={20} /></button>
            </div>
            {modalError ? <p className="font-body" style={{ background: '#FEF2F2', color: '#991B1B', borderRadius: '10px', padding: '10px' }}>{modalError}</p> : null}

            <div style={{ display: 'grid', gap: '16px' }}>
              <div>
                <label className="font-body" style={{ display: 'block', marginBottom: '8px', fontWeight: 700 }}>Customer Phone</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input value={customerPhone} onChange={(event) => setCustomerPhone(event.target.value)} className="font-body" style={{ flex: 1, height: '42px', border: `1.5px solid ${colors.border}`, borderRadius: '10px', padding: '0 12px' }} />
                  <button type="button" onClick={() => void handleSearchCustomer()} style={{ border: 'none', background: colors.textPrimary, color: colors.surface, borderRadius: '10px', padding: '0 14px', cursor: 'pointer' }}>Search</button>
                </div>
                {customer ? (
                  <p className="font-body" style={{ color: '#15803D', fontWeight: 700 }}>Selected: {customer.name || customer.phone}</p>
                ) : (
                  <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                    <input value={customerName} onChange={(event) => setCustomerName(event.target.value)} placeholder="New customer name" className="font-body" style={{ flex: 1, height: '40px', border: `1.5px solid ${colors.border}`, borderRadius: '10px', padding: '0 12px' }} />
                    <button type="button" onClick={() => void handleCreateCustomer()} style={{ border: '1px solid #D6B97B', background: 'transparent', color: '#D6B97B', borderRadius: '10px', padding: '0 14px', cursor: 'pointer' }}>Create</button>
                  </div>
                )}
              </div>

              <div>
                <label className="font-body" style={{ display: 'block', marginBottom: '8px', fontWeight: 700 }}>Order Number</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input value={orderSearch} onChange={(event) => setOrderSearch(event.target.value)} placeholder="Search order number" className="font-body" style={{ flex: 1, height: '42px', border: `1.5px solid ${colors.border}`, borderRadius: '10px', padding: '0 12px' }} />
                  <button type="button" onClick={() => void handleSearchOrders()} style={{ border: 'none', background: colors.textPrimary, color: colors.surface, borderRadius: '10px', padding: '0 14px', cursor: 'pointer' }}>Find</button>
                </div>
                <select value={formData.order_id} onChange={(event) => setFormData((prev) => ({ ...prev, order_id: event.target.value }))} className="font-body" style={{ marginTop: '10px', width: '100%', height: '42px', border: `1.5px solid ${colors.border}`, borderRadius: '10px', padding: '0 12px' }}>
                  <option value="">No order selected</option>
                  {orders.map((order) => <option key={order.id} value={order.id}>{order.order_number} {order.customer_name ? `- ${order.customer_name}` : ''}</option>)}
                </select>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <select value={formData.category} onChange={(event) => setFormData((prev) => ({ ...prev, category: event.target.value }))} className="font-body" style={{ height: '42px', border: `1.5px solid ${colors.border}`, borderRadius: '10px', padding: '0 12px' }}>
                  {categories.map((category) => <option key={category} value={category}>{toTitle(category)}</option>)}
                </select>
                <div style={{ display: 'flex', gap: '6px' }}>
                  {priorities.map((priority) => {
                    const selected = formData.priority === priority
                    const palette = priorityStyle(priority)
                    return (
                      <button key={priority} type="button" onClick={() => setFormData((prev) => ({ ...prev, priority }))} style={{ flex: 1, border: `1.5px solid ${selected ? palette.border : colors.border}`, background: selected ? palette.background : colors.bgCard, color: selected ? palette.color : colors.textSecondary, borderRadius: '10px', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}>
                        {palette.label}
                      </button>
                    )
                  })}
                </div>
              </div>
              <textarea value={formData.description} onChange={(event) => setFormData((prev) => ({ ...prev, description: event.target.value }))} placeholder="Describe the issue..." className="font-body" style={{ minHeight: '110px', border: `1.5px solid ${colors.border}`, borderRadius: '10px', padding: '12px', resize: 'vertical' }} />
              <button type="button" disabled={saving} onClick={() => void handleCreateComplaint()} className="font-body" style={{ height: '44px', border: 'none', borderRadius: '999px', background: '#D6B97B', color: colors.textPrimary, fontWeight: 700, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
                {saving ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {selectedComplaint ? (
        <>
          <button type="button" aria-label="Close complaint detail" onClick={() => setSelectedComplaint(null)} style={{ position: 'fixed', inset: 0, left: '240px', background: 'rgba(0,0,0,0.3)', border: 'none', zIndex: 40 }} />
          <aside style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: '420px', background: colors.bgCard, borderLeft: `1px solid ${colors.border}`, zIndex: 50, overflowY: 'auto' }}>
            <div style={{ position: 'sticky', top: 0, background: colors.bgCard, borderBottom: `1px solid ${colors.border}`, padding: '20px 24px', display: 'flex', justifyContent: 'space-between' }}>
              <div>
                <h2 className="font-display" style={{ margin: 0, fontSize: '18px', color: colors.textPrimary }}>Complaint Detail</h2>
                <p className="font-body" style={{ margin: '4px 0 0', color: colors.textMuted }}>{formatDate(selectedComplaint.created_at)}</p>
              </div>
              <button type="button" onClick={() => setSelectedComplaint(null)} style={{ border: 'none', background: 'transparent', cursor: 'pointer' }}><X size={20} /></button>
            </div>
            <div style={{ padding: '24px', display: 'grid', gap: '20px' }}>
              <section>
                <h3 className="font-body" style={{ margin: '0 0 8px', fontSize: '12px', color: colors.textMuted, textTransform: 'uppercase' }}>Customer</h3>
                <p className="font-body" style={{ margin: 0, fontWeight: 700, color: colors.textPrimary }}>{selectedComplaint.customer_name || 'Customer'}</p>
                <p className="font-body" style={{ margin: '4px 0 0', color: colors.textSecondary }}>{selectedComplaint.customer_phone || '-'}</p>
                <p className="font-body" style={{ margin: '10px 0 0', color: colors.textSecondary }}>Order: {selectedComplaint.order_number || '-'}</p>
              </section>
              <section>
                <h3 className="font-body" style={{ margin: '0 0 8px', fontSize: '12px', color: colors.textMuted, textTransform: 'uppercase' }}>Complaint Timeline</h3>
                {(selectedComplaint.timeline || [{ label: 'Complaint logged', created_at: selectedComplaint.created_at }, { label: `Current status: ${toTitle(selectedComplaint.status)}`, created_at: selectedComplaint.updated_at }]).map((event, index) => (
                  <div key={`${event.label}-${index}`} style={{ borderLeft: '2px solid #D6B97B', padding: '0 0 14px 12px' }}>
                    <p className="font-body" style={{ margin: 0, fontWeight: 700, color: colors.textPrimary }}>{event.label || 'Status update'}</p>
                    <p className="font-body" style={{ margin: '2px 0 0', fontSize: '12px', color: colors.textMuted }}>{formatDate(event.created_at)}</p>
                  </div>
                ))}
              </section>
              <select value={detailStatus} onChange={(event) => setDetailStatus(event.target.value as ComplaintStatus)} className="font-body" style={{ height: '42px', border: `1.5px solid ${colors.border}`, borderRadius: '10px', padding: '0 12px' }}>
                {statuses.map((status) => <option key={status} value={status}>{toTitle(status)}</option>)}
              </select>
              <textarea value={resolution} onChange={(event) => setResolution(event.target.value)} placeholder="Resolution notes..." className="font-body" style={{ minHeight: '120px', border: `1.5px solid ${colors.border}`, borderRadius: '10px', padding: '12px', resize: 'vertical' }} />
              <input type="number" value={refundAmount} onChange={(event) => setRefundAmount(event.target.value)} placeholder="Refund amount if applicable" className="font-body" style={{ height: '42px', border: `1.5px solid ${colors.border}`, borderRadius: '10px', padding: '0 12px' }} />
              <button type="button" onClick={() => void handleUpdateComplaint(false)} disabled={saving} className="font-body" style={{ height: '42px', border: '1px solid #D6B97B', color: '#D6B97B', background: 'transparent', borderRadius: '10px', fontWeight: 700, cursor: 'pointer' }}>
                Update Status
              </button>
              <button type="button" onClick={() => void handleUpdateComplaint(true)} disabled={saving} className="font-body" style={{ height: '46px', border: 'none', background: '#D6B97B', color: colors.textPrimary, borderRadius: '999px', fontWeight: 700, cursor: 'pointer' }}>
                Resolve
              </button>
              <button type="button" onClick={openWhatsApp} className="font-body" style={{ height: '42px', border: 'none', background: '#25D366', color: colors.bgCard, borderRadius: '10px', fontWeight: 700, cursor: 'pointer', display: 'inline-flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}>
                <MessageCircle size={16} />
                WhatsApp Customer
              </button>
            </div>
          </aside>
        </>
      ) : null}
    </div>
  )
}
